import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { get } from '@ember/helper';
import { macroCondition, isDevelopingApp } from '@embroider/macros';
import { task } from 'ember-concurrency';
import { not, or, eq, and, notEq } from 'ember-truth-helpers';
import { t } from 'ember-intl';
import { queue } from '@nullvoxpopuli/ember-composable-helpers';
import set from 'ember-set-helper/helpers/set';
import {
  HdsPageHeader,
  HdsDropdown,
  HdsSeparator,
  HdsModal,
  HdsButtonSet,
  HdsButton,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  OperationRefreshRoute,
  DetailSection,
  OperationFailureForResource,
  Loading,
  FlexGrid,
  Typography,
  ModalConfirm,
} from 'core/components';
import { urlFor, resourceTypeFor, resourceIsBusy } from 'core/helpers';
import { boxPadding } from 'core/modifiers';
import { TERRAFORM_IMPORTS } from 'core/utils/constants';
import ConnectionInstructionsAccordion from 'networks-common/components/networks/connection-instructions-accordion';
import List from 'hcp/components/page/hvn-routes/list';
import ConnectionInstructionsBanner from 'networks-common/components/networks/connection-instructions-banner';
import ContentTabs from 'hcp/components/page/hvn-transit-gateway-attachments/detail/content-tabs';
import ConnectionDetails from 'networks-common/components/networks/connection-details';

/**
 *
 * `tab` will be the selected tab;
 * @argument tab;
 * @type {String}
 *
 */
/**
 *
 * `product` such as consul or vault;
 * @argument product;
 * @type {String}
 *
 */
/**
 *
 * `model` will be a model containing the attachment detail;
 * @argument model;
 * @type {Object}
 *
 */
/**
 *
 * `PageHvnTransitGatewayAttachmentsDetail` renders the HVN TGW attachments Detail page.
 *
 *
 * ```
 * <Page::HvnTransitGatewayAttachments::Detail
 *   @model={{model}}
 *   @tab={{tab}}
 *   @product={{product}}
 * />
 * ```
 *
 * @class PageHvnTransitGatewayAttachmentsDetail
 *
 */
export default class PageHvnTransitGatewayAttachmentsDetail extends Component {
  @service api;
  @service flashMessages;
  @service intl;
  @service operation;

  @tracked showDeleteModal = false;
  @tracked allowDelete = false;

  get instructionsId() {
    return 'instructions';
  }

  get terraformImport() {
    return TERRAFORM_IMPORTS.awsTransitGateway({
      hvnId: this.args.model.network.id,
      transitGatewayId: this.args.model.tgwAttachment.id,
    });
  }

  deleteTransitGatewayAttachment = task(async (evt) => {
    evt.preventDefault();
    let hvnId = this.args.model.network.id;
    let { organizationId, projectId } = this.args.model.network.location;
    let { id } = this.args.model.tgwAttachment;
    let operation;

    try {
      let resp = await this.api.network.deleteTGWAttachment(
        organizationId,
        projectId,
        hvnId,
        id
      );
      this.flashMessages.success(
        this.intl.t(
          'components.page.hvn-transit-gateway-attachments.delete.success.title'
        )
      );
      operation = resp.operation;
    } catch (e) {
      if (macroCondition(isDevelopingApp())) {
        console.error(e);
      }
      throw e;
    }
    this.showDeleteModal = false;
    this.operation.addOperation(operation);
  });

  <template>
    <CoreLayoutMain>
      <:header>
        <HdsPageHeader data-test-transit-gateway-attachment-header as |PH|>
          <PH.Breadcrumb>
            <BreadcrumbsContainer />
          </PH.Breadcrumb>
          <PH.Title>
            {{@model.tgwAttachment.id}}
          </PH.Title>
          {{#if
            (not
              (or
                (eq @model.tgwAttachment.state "DELETING")
                (eq @model.tgwAttachment.state "CREATING")
              )
            )
          }}
            <PH.Actions>
              <HdsDropdown as |dd|>
                <dd.ToggleButton
                  @text={{t "components.form.manage"}}
                  data-test-dropdown
                />
                <dd.Title @text={{t "components.import-to-terraform.title"}} />
                <dd.Description
                  @text={{t "components.import-to-terraform.body"}}
                />
                <dd.Interactive
                  @href="https://www.terraform.io/docs/cli/import/usage.html"
                  @icon="docs-link"
                  @isHrefExternal={{true}}
                  @text={{t "components.import-to-terraform.docs-link"}}
                />
                <dd.CopyItem @text={{this.terraformImport}} />
                <dd.Separator />
                <dd.Interactive
                  @color="critical"
                  @icon="trash"
                  @text={{t "components.form.delete"}}
                  id="model-dialog-open-button-{{@model.tgwAttachment.id}}"
                  data-test-delete-button
                  {{on "click" (set this "showDeleteModal" true)}}
                />
              </HdsDropdown>
            </PH.Actions>
          {{/if}}
        </HdsPageHeader>
      </:header>
      <:body>
        <div class="hvnsTransitGatewayAttachmentsDetail">
          <OperationRefreshRoute
            @route="cloud.orgs.detail.projects.detail.hvns.detail.transit-gateway-attachments"
            @routeFallback={{urlFor
              "cloud.orgs.detail.projects.detail.hvns.detail.transit-gateway-attachments.list"
            }}
            @model={{@model.tgwAttachment}}
            @resourceType={{resourceTypeFor "network-tgw-attachment"}}
          />
          {{#if
            (and
              (resourceIsBusy
                @model.tgwAttachment
                type=(resourceTypeFor "network-tgw-attachment")
              )
              (notEq @model.tgwAttachment.state "PENDING_ACCEPTANCE")
            )
          }}
            <div>
              <OperationFailureForResource
                @resource={{@model.tgwAttachment}}
                @resourceType={{resourceTypeFor "network-tgw-attachment"}}
              />
              <Loading data-test-loading as |L|>
                <L.Elapsed />
                <L.Header>
                  {{if
                    (eq @model.tgwAttachment.state "DELETING")
                    (t
                      "components.page.hvn-transit-gateway-attachments.detail.deleting.header"
                    )
                  }}
                  {{if
                    (eq @model.tgwAttachment.state "CREATING")
                    (t
                      "components.page.hvn-transit-gateway-attachments.detail.initializing.header"
                    )
                  }}
                </L.Header>
                <L.Message>
                  {{if
                    (eq @model.tgwAttachment.state "DELETING")
                    (t
                      "components.page.hvn-transit-gateway-attachments.detail.deleting.message"
                    )
                  }}
                  {{if
                    (eq @model.tgwAttachment.state "CREATING")
                    (t
                      "components.page.hvn-transit-gateway-attachments.detail.initializing.message"
                    )
                  }}
                </L.Message>
              </Loading>
            </div>
          {{else}}
            <div data-test-transit-gateway-attachment-details>
              <ConnectionInstructionsBanner
                @connection={{@model.tgwAttachment}}
                @id={{this.instructionsId}}
                @routes={{@model.routes}}
              />

              <FlexGrid as |G|>
                <G.Item @xs="12" @sm="12" @md="12" @lg="6">
                  <DetailSection
                    @title={{t
                      "components.page.hvn-transit-gateway-attachments.detail.attachment-details.title"
                    }}
                  >
                    <ConnectionDetails @connection={{@model.tgwAttachment}} />
                  </DetailSection>
                </G.Item>
                <G.Item @xs="12" @sm="12" @md="12" @lg="12">
                  {{! template-lint-disable no-nested-interactive }}
                  {{! this rule is disabled to allow for labels being used for tabs within <details> }}
                  {{! template-lint-disable no-duplicate-id }}
                  <ConnectionInstructionsAccordion
                    data-test-instructions-accordion
                    @connection={{@model.tgwAttachment}}
                    @id={{this.instructionsId}}
                    @isInitiallyOpen={{true}}
                  >
                    <ContentTabs
                      @model={{@model}}
                      @tab={{@model.tab}}
                      @product={{@model.product}}
                    />
                  </ConnectionInstructionsAccordion>
                </G.Item>
              </FlexGrid>
              {{#let (get this.alerts @model.tgwAttachment.state) as |alert|}}
                {{#unless (eq alert.variant "warning")}}
                  <div {{boxPadding "xl 0 0 0"}}>
                    <HdsSeparator @spacing="0" />
                  </div>
                {{/unless}}
              {{/let}}
            </div>
            {{! HVN Routes Table List }}
            <List @model={{@model}} />
          {{/if}}
        </div>

        {{! Delete TGW Attachment }}
        {{#if this.showDeleteModal}}
          <HdsModal
            @color="critical"
            @onClose={{queue
              (set this "showDeleteModal")
              (set this "allowDelete")
            }}
            as |M|
          >
            <M.Header @icon="alert-triangle">
              {{t
                "components.page.hvn-transit-gateway-attachments.list.delete-modal.title"
              }}
            </M.Header>
            <M.Body>
              <Typography
                @variant="body1"
                class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
                {{boxPadding "0 0 md 0"}}
              >
                {{t
                  "components.page.hvn-transit-gateway-attachments.list.delete-modal.warning"
                  tgwId=@model.tgwAttachment.providerData.awsData.tgwId
                  htmlSafe=true
                }}
              </Typography>
              <Typography
                @variant="body1"
                class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
                {{boxPadding "0 0 md 0"}}
              >
                {{t
                  "components.page.hvn-transit-gateway-attachments.list.delete-modal.recreation"
                }}
              </Typography>
              <form
                id="delete"
                aria-label={{t
                  "components.page.hvn-transit-gateway-attachments.list.delete-modal.recreation"
                }}
                class="hcp-hds-form"
                {{on "submit" this.deleteTransitGatewayAttachment.perform}}
              >
                <ModalConfirm @onAllowedChange={{set this "allowDelete"}} />
              </form>
            </M.Body>
            <M.Footer as |F|>
              <HdsButtonSet>
                <HdsButton
                  @color="critical"
                  @text={{t "components.form.delete"}}
                  disabled={{or
                    (not this.allowDelete)
                    this.deleteTransitGatewayAttachment.isRunning
                  }}
                  form="delete"
                  type="submit"
                  data-test-confirm-delete
                />
                <HdsButton
                  @color="secondary"
                  @text={{t "components.form.cancel"}}
                  disabled={{this.deleteTransitGatewayAttachment.isRunning}}
                  {{on "click" F.close}}
                />
              </HdsButtonSet>
            </M.Footer>
          </HdsModal>
        {{/if}}
      </:body>
    </CoreLayoutMain>
  </template>
}
